import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import RankBadge from './RankBadge';
import { 
  fadeInUp, 
  fadeInLeft, 
  fadeInRight, 
  dramaticEntrance,
  pulse
} from '../styles/animations';

// Styled components for the team card
const CardContainer = styled(motion.div).attrs(props => ({
  // Filter out custom props that shouldn't be passed to DOM
  // by not including them in the attrs
}))`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bgLightGradient};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.space.xl}; /* Increased padding from lg to xl */
  margin-bottom: ${({ theme }) => theme.space.lg};
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: ${({ $isPodium }) => $isPodium ? '520px' : '455px'}; /* Increased by ~30% */
  min-height: ${({ $isPodium }) => $isPodium ? '275px' : '250px'}; /* Increased by ~25% */
  transform: ${({ $isPodium }) => $isPodium ? 'scale(1.05)' : 'scale(1)'};
  
  /* Rank-specific styling */
  ${({ rank, theme }) => {
    switch (rank) {
      case 1:
        return `
          border: 2px solid ${theme.colors.gold};
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
          
          &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: radial-gradient(circle at top right, rgba(255, 215, 0, 0.15), transparent 70%);
            pointer-events: none;
          }
        `;
      case 2:
        return `
          border: 2px solid ${theme.colors.silver};
          box-shadow: 0 0 15px rgba(192, 192, 192, 0.3);
          
          &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: radial-gradient(circle at top right, rgba(192, 192, 192, 0.15), transparent 70%);
            pointer-events: none;
          }
        `;
      case 3:
        return `
          border: 2px solid ${theme.colors.bronze};
          box-shadow: 0 0 15px rgba(205, 127, 50, 0.3);
          
          &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: radial-gradient(circle at top right, rgba(205, 127, 50, 0.15), transparent 70%);
            pointer-events: none;
          }
        `;
      default:
        return `
          border: 2px solid ${theme.colors.fourth};
          box-shadow: 0 0 10px rgba(85, 85, 85, 0.3);
          
          &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: radial-gradient(circle at top right, rgba(85, 85, 85, 0.15), transparent 70%);
            pointer-events: none;
          }
        `;
    }
  }}
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    max-width: 585px; /* Increased from 450px by ~30% */
    min-height: 180px; /* Increased from 150px by ~20% */
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.md};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-right: ${({ theme }) => theme.space.lg};
    margin-bottom: 0;
  }
`;

const TeamInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TeamName = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes['2xl']}; /* Increased from xl to 2xl */
  margin-bottom: ${({ theme }) => theme.space.sm}; /* Increased from xs to sm */
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* Increased from 2 to 3 lines */
  -webkit-box-orient: vertical;
  word-break: break-word;
  line-height: 1.3; /* Added line height for better readability */
  
  ${({ rank, theme }) => {
    switch (rank) {
      case 1:
        return `
          background: linear-gradient(to right, ${theme.colors.gold}, #FFC800);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
        `;
      case 2:
        return `
          background: linear-gradient(to right, ${theme.colors.silver}, #A0A0A0);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          text-shadow: 0 0 10px rgba(192, 192, 192, 0.3);
        `;
      case 3:
        return `
          background: linear-gradient(to right, ${theme.colors.bronze}, #B06728);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          text-shadow: 0 0 10px rgba(205, 127, 50, 0.3);
        `;
      default:
        return `
          color: ${theme.colors.textPrimary};
          font-size: ${({ theme }) => theme.fontSizes.lg};
        `;
    }
  }}
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: left;
    font-size: ${({ $isPodium, theme }) => $isPodium ? theme.fontSizes['3xl'] : theme.fontSizes['2xl']}; /* Increased sizes */
  }
`;

const ScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${({ theme }) => theme.space.sm};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    align-items: flex-start;
  }
`;

const ScoreLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const ScoreValue = styled(motion.div)`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.fontSizes['2xl']}; /* Increased from xl to 2xl */
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
  
  ${({ rank, theme }) => {
    switch (rank) {
      case 1:
        return `
          color: ${theme.colors.gold};
        `;
      case 2:
        return `
          color: ${theme.colors.silver};
        `;
      case 3:
        return `
          color: ${theme.colors.bronze};
        `;
      default:
        return `
          color: ${theme.colors.primary};
        `;
    }
  }}
`;

// Get animation variant based on rank
const getAnimationVariant = (rank) => {
  switch (rank) {
    case 1:
      return dramaticEntrance;
    case 2:
      return fadeInRight;
    case 3:
      return fadeInLeft;
    default:
      return fadeInUp;
  }
};

/**
 * TeamRankCard Component
 * Displays a team's rank, name, and total score with appropriate styling
 * 
 * @param {Object} props - Component props
 * @param {Object} props.team - Team data object
 * @param {boolean} props.animate - Whether to animate the card
 * @param {boolean} props.visible - Whether the card should be visible
 */
const TeamRankCard = ({ team, animate = true, visible = true }) => {
  const { rank, name, totalScore } = team;
  const animationVariant = getAnimationVariant(rank);
  const isPodium = rank <= 3; // Check if this is a podium team (1st, 2nd, or 3rd)
  
  return (
    <CardContainer
      rank={rank}
      $isPodium={isPodium}
      initial={animate ? "hidden" : "visible"}
      animate={visible ? "visible" : "hidden"}
      variants={animationVariant}
    >
      <BadgeContainer>
        <RankBadge rank={rank} animate={animate} />
      </BadgeContainer>
      
      <TeamInfoContainer>
        <TeamName rank={rank}>{name}</TeamName>
        
        <ScoreContainer>
          <ScoreLabel>Total Score</ScoreLabel>
          <ScoreValue 
            rank={rank}
            variants={pulse}
            initial={animate ? "hidden" : "visible"}
            animate={visible ? "visible" : "hidden"}
          >
            {totalScore} pts
          </ScoreValue>
        </ScoreContainer>
      </TeamInfoContainer>
    </CardContainer>
  );
};

export default TeamRankCard;